<!--
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2025-08-06 09:39:23 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2025-09-30 16:37:54 +0800
 * @FilePath     : /izt_web/src/views/ai/components/common/hold-talk-wave.vue
 * @Description  : 说话中
-->
<template>
  <canvas ref="canvas" class="wave-canvas"></canvas>
</template>

<script>
export default {
  name: 'CanvasMicVisualizer',
  props: {
    barCount: { // 动画柱子的数量
      type: Number,
      default: 28,
    },
    spacing: { // 柱子的间距
      type: Number,
      default: 3,
    },
    barWidth: { // 柱子的宽度
      type: Number,
      default: 5,
    },
    loading: { // 加载中状态
      type: Boolean,
      default: false,
    },
    loadingSpeed: { // loading动画时长
      type: Number,
      default: 1000,
    },

    barColors: { // 柱子的颜色
      type: Array,
      default: () => ['#4f50f6', '#be47ef'], // 默认紫色渐变
    },

    sensitivity: { // 声音敏感度
      type: Number,
      default: 1, // 默认1.5倍放大，可根据需求调节
    }
  },
  data() {
    return {
      ctx: null,
      audioContext: null,
      analyser: null,
      sourceNode: null,
      animationId: null,
      canvasWidth: 300,
      canvasHeight: 26,
      currentHeights: [], // 渐变高度用
      wavePhase: 0,
    }
  },
  mounted() {
    const canvas = this.$refs.canvas
    const rect = canvas.getBoundingClientRect()

    // 高分屏适配
    const dpr = window.devicePixelRatio || 1
    this.canvasWidth = rect.width
    this.canvasHeight = rect.height

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${rect.height}px`

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)

    this.ctx = ctx // 把 ctx 存下来供其他方法使用

    this.initBars()
    this.loading ? this.startWaveLoading() : this.initAudio()
  },
  watch: {
    loading(val) {
      setTimeout(() => this.stop(), 300)
      this.initBars()
      val ? this.startWaveLoading() : this.initAudio()
    },
  },
  beforeDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId)
    this.animationId = null
    setTimeout(() => this.stop(), 300)
  },
  methods: {
    initBars() {
      this.currentHeights = Array(this.barCount).fill(4)
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
      this.drawBars(this.currentHeights)
    },

    // 监听说话
    async initAudio() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.analyser = this.audioContext.createAnalyser()
        this.sourceNode = this.audioContext.createMediaStreamSource(stream)
        this.sourceNode.connect(this.analyser)

        this.analyser.fftSize = 64
        const bufferLength = this.analyser.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)

        const ctx = this.ctx
        const half = Math.floor(this.barCount / 2)
        const barWidth = this.barWidth
        const spacing = this.spacing
        const totalWidth = this.barCount * barWidth + (this.barCount - 1) * spacing
        const offsetX = (this.canvasWidth - totalWidth) / 2

        const draw = () => {
          if (this.loading) return
          this.animationId = requestAnimationFrame(draw)
          if (this.analyser && this.analyser.getByteFrequencyData) this.analyser.getByteFrequencyData(dataArray)

          // 更新 currentHeights
          const half = Math.floor(this.barCount / 2)
          for (let i = 0; i < half; i++) {
            const v = dataArray[i] / 255
            const targetHeight = Math.max(4, v * this.canvasHeight * this.sensitivity)
            const smoothing = 0.3
            const newHeight = this.currentHeights[i] * (1 - smoothing) + targetHeight * smoothing
            this.currentHeights[i] = newHeight
            this.currentHeights[this.barCount - 1 - i] = newHeight
          }

          this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
          this.drawBars(this.currentHeights)
        }

        draw()
      } catch (e) {
        console.error('麦克风初始化失败:', e)
      }
    },
    // 绘制矩形柱子
    drawRoundBar(ctx, x, y, width, height, radius = 3) {
      ctx.beginPath()
      const r = Math.min(radius, height / 2, width / 2)
      ctx.moveTo(x, y + r)
      ctx.arcTo(x, y, x + width, y, r)
      ctx.arcTo(x + width, y, x + width, y + height, r)
      ctx.arcTo(x + width, y + height, x, y + height, r)
      ctx.arcTo(x, y + height, x, y, r)
      ctx.closePath()
      ctx.fill()
    },
    // loading
    startWaveLoading() {
      const ctx = this.ctx
      const barWidth = this.barWidth
      const spacing = this.spacing
      const totalWidth = this.barCount * barWidth + (this.barCount - 1) * spacing
      const offsetX = (this.canvasWidth - totalWidth) / 2

      const base = this.canvasHeight * 0.4
      const amp = this.canvasHeight * 0.4
      const half = Math.floor(this.barCount / 2)

      let lastTs = 0

      const animate = (ts) => {
        this.animationId = requestAnimationFrame(animate)
        const delta = ts - lastTs
        lastTs = ts
        this.wavePhase = (this.wavePhase + (2 * Math.PI * delta) / this.loadingSpeed) % (2 * Math.PI)

        const heights = []
        for (let i = 0; i < this.barCount; i++) {
          const phase = (this.wavePhase + (i / this.barCount) * 2 * Math.PI) % (2 * Math.PI)
          const height = base + amp * Math.sin(phase)
          heights.push(height)
        }

        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
        this.drawBars(heights)
      }
      animate(0)
    },

    drawBars(heights) {
      const ctx = this.ctx
      const barWidth = this.barWidth
      const spacing = this.spacing
      const totalWidth = this.barCount * barWidth + (this.barCount - 1) * spacing
      const offsetX = (this.canvasWidth - totalWidth) / 2

      ctx.fillStyle = this.getFillStyle(ctx)

      heights.forEach((height, i) => {
        const x = offsetX + i * (barWidth + spacing)
        const y = (this.canvasHeight - height) / 2
        this.drawRoundBar(ctx, x, y, barWidth, height)
      })
    },
    // 提取颜色的方法
    getFillStyle(ctx) {
      if (this.barColors.length === 1) {
        return this.barColors[0]
      }
      const gradient = ctx.createLinearGradient(0, 0, this.canvasWidth, 0)
      gradient.addColorStop(0, this.barColors[0])
      gradient.addColorStop(1, this.barColors[1])
      return gradient
    },
    // 停止
    stop() {
      if (this.audioContext) this.audioContext.close()
      if (this.sourceNode?.mediaStream) {
        this.sourceNode.mediaStream.getTracks().forEach(t => t.stop())
      }
      this.audioContext = null
      this.sourceNode = null
      this.analyser = null
    }
  }
}
</script>

<style scoped>
.wave-canvas {
  width: 100%;
  height: 26px;
  display: block;
  border-radius: 16px;
  background: transparent;
  mask-image: linear-gradient(to right, transparent 0%, rgb(255, 255, 255) 30%, rgb(255, 255, 255) 30%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, rgb(255, 255, 255) 30%, rgb(255, 255, 255) 30%, transparent 100%);
}
</style>
